import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Tag,
  X,
  Save,
  FolderOpen,
  AlertTriangle,
  Package,
  GripVertical,
  Hash,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
  useSortable,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useProducts } from '../../context/ProductContext';
import { fuzzyMatch } from '../../lib/fuzzySearch';

const formatCategoryImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://')) return url;
  const backendBase = (process.env.REACT_APP_API_URL || 'http://localhost:8001/api').replace(/\/api\/?$/, '');
  if (url.startsWith('/')) {
    return `${backendBase}${url}`;
  }
  return url.replace(/http:\/\/localhost:3000/g, backendBase);
};

const CategoryManagement = () => {
  const { categories, products, addCategory, uploadCategoryImage, updateCategory, deleteCategory, reorderCategories } = useProducts();
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Split the real categories (drop the synthetic 'all') into the rankable set
  // and the pinned "Uncategorized", which stays out of the drag arrangement.
  const rankable = useMemo(
    () => categories.filter(c => c.id !== 'all' && c.name?.toLowerCase() !== 'uncategorized'),
    [categories]
  );
  const uncategorized = useMemo(
    () => categories.find(c => c.id !== 'all' && c.name?.toLowerCase() === 'uncategorized') || null,
    [categories]
  );
  const rankableById = useMemo(() => new Map(rankable.map(c => [c.id, c])), [rankable]);

  // Local id order that drives the sortable grid. Seeded from context (already
  // in sortOrder order) and re-synced whenever categories are added/removed or a
  // reorder is persisted — so an edit never leaves the grid out of order.
  const [orderedIds, setOrderedIds] = useState(() => rankable.map(c => c.id));
  useEffect(() => { setOrderedIds(rankable.map(c => c.id)); }, [rankable]);

  const sensors = useSensors(
    // A small activation distance lets the edit/delete buttons and the grip be
    // clicked without accidentally starting a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Dragging to reorder only makes sense against the full list; while the admin
  // is searching (a filtered subset) the cards are shown static.
  const isReorderEnabled = !searchTerm.trim();

  const matchesSearch = useCallback((cat) =>
    !searchTerm.trim() ||
    fuzzyMatch(searchTerm, cat.name) ||
    (cat.description && fuzzyMatch(searchTerm, cat.description)),
  [searchTerm]);

  // Get product count for each category
  const getProductCount = (categoryId) => {
    return products.filter(p => p.category === categoryId).length;
  };

  // Ranked cards in display order, each tagged with its 1-based priority rank
  // (the badge shows this true global rank even when the list is filtered).
  const rankedList = useMemo(
    () => orderedIds
      .map((id, i) => ({ category: rankableById.get(id), rank: i + 1 }))
      .filter(entry => entry.category),
    [orderedIds, rankableById]
  );
  const visibleRanked = isReorderEnabled ? rankedList : rankedList.filter(e => matchesSearch(e.category));
  const uncategorizedVisible = uncategorized && matchesSearch(uncategorized);
  const isEmpty = visibleRanked.length === 0 && !uncategorizedVisible;

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setOrderedIds(prev => {
      const oldIndex = prev.indexOf(active.id);
      const newIndex = prev.indexOf(over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      // Persist; the context optimistically re-orders and then re-syncs from the
      // server, so the shop page reflects the new order too.
      reorderCategories(next).catch(() => toast.error('Could not save the new order. Please try again.'));
      return next;
    });
  };

  const handleDelete = (category) => {
    const assignedProducts = products.filter(p => p.category === category.id);
    setDeleteTarget({ category, assignedProducts });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Category Management</h2>
          <p className="text-gray-600 mt-1">Manage product categories for your store</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-[#548B2E] text-white px-4 py-2 rounded-lg hover:bg-[#3f6b20] transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add Category
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E]/50"
          />
        </div>
      </div>

      {/* Reorder hint */}
      {!isEmpty && (
        <p className="text-sm text-gray-500 -mt-2">
          {isReorderEnabled ? (
            <>Drag the cards to set the order categories appear in on the shop page. The badge on each card's bottom-right shows its priority rank.</>
          ) : (
            <>Reordering is paused while searching — clear the search box to drag categories.</>
          )}
        </p>
      )}

      {/* Categories Grid */}
      {isEmpty ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No categories found</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm
              ? 'Try adjusting your search term'
              : 'Get started by adding your first category'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 bg-[#548B2E] text-white px-4 py-2 rounded-lg hover:bg-[#3f6b20] transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Category
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {isReorderEnabled ? (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={orderedIds} strategy={rectSortingStrategy}>
                  {rankedList.map(({ category, rank }) => (
                    <SortableCategoryCard
                      key={category.id}
                      category={category}
                      rank={rank}
                      productCount={getProductCount(category.id)}
                      onEdit={() => setEditingCategory(category)}
                      onDelete={() => handleDelete(category)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            ) : (
              visibleRanked.map(({ category, rank }) => (
                <CategoryCard
                  key={category.id}
                  category={category}
                  rank={rank}
                  productCount={getProductCount(category.id)}
                  onEdit={() => setEditingCategory(category)}
                  onDelete={() => handleDelete(category)}
                />
              ))
            )}
          </div>

          {/* Uncategorized — pinned, kept out of the ranking */}
          {uncategorizedVisible && (
            <div className="pt-2">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Excluded from ranking</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <CategoryCard
                  category={uncategorized}
                  isUncategorized
                  productCount={getProductCount(uncategorized.id)}
                  onEdit={() => setEditingCategory(uncategorized)}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <DeleteCategoryModal
          category={deleteTarget.category}
          assignedProducts={deleteTarget.assignedProducts}
          onClose={() => setDeleteTarget(null)}
          onConfirm={async () => {
            await deleteCategory(deleteTarget.category.id);
            setDeleteTarget(null);
          }}
        />
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || editingCategory) && (
        <CategoryModal
          category={editingCategory}
          onClose={() => {
            setShowAddModal(false);
            setEditingCategory(null);
          }}
          onSave={async (payload) => {
            try {
              if (editingCategory) {
                await updateCategory(editingCategory.id, payload);
                toast.success('Category updated successfully');
              } else {
                await addCategory(payload);
                toast.success('Category created successfully');
              }
              setShowAddModal(false);
              setEditingCategory(null);
            } catch (err) {
              toast.error(err.message || 'Operation failed');
            }
          }}
        />
      )}
    </div>
  );
};

// Presentational category card. `handle` (the drag grip) is slotted in only for
// the sortable variant; `rank` drives the bottom-right badge. "Uncategorized"
// renders with no grip, no delete, and a muted "Not ranked" badge instead.
const CategoryCard = React.forwardRef(function CategoryCard(
  { category, productCount, rank, isUncategorized = false, handle = null, isDragging = false, style, onEdit, onDelete },
  ref
) {
  const [imageError, setImageError] = useState(false);
  const catImage = formatCategoryImageUrl(category.imageUrl || category.image_url);

  useEffect(() => {
    setImageError(false);
  }, [catImage]);

  return (
    <div
      ref={ref}
      style={style}
      className={`flex flex-col h-full bg-white rounded-xl shadow-sm p-6 transition-shadow ${
        isDragging ? 'shadow-xl ring-2 ring-[#548B2E]/40' : 'hover:shadow-md'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-1.5">
          {handle}
          <div className="w-12 h-12 bg-[#f2f8eb] rounded-lg flex items-center justify-center overflow-hidden border border-gray-100 flex-shrink-0">
            <img
              src={catImage && !imageError ? catImage : '/category_placeholder.png'}
              alt={category.name}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onEdit}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit category"
          >
            <Edit className="w-4 h-4" />
          </button>
          {!isUncategorized && (
            <button
              onClick={onDelete}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete category"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <h3 className="font-semibold text-gray-800 text-lg mb-1">{category.name}</h3>
      {category.description && (
        <p className="text-gray-500 text-sm mb-3 line-clamp-2">{category.description}</p>
      )}

      <div className="mt-auto flex items-center justify-between gap-2 pt-3 text-sm">
        <div className="flex items-center gap-2">
          <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full whitespace-nowrap">
            {productCount} products
          </span>
          <span className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full font-mono text-xs">
            {category.id}
          </span>
        </div>
        {isUncategorized ? (
          <span className="px-2.5 py-1 rounded-full text-[11px] font-medium text-gray-400 bg-gray-50 border border-gray-200 whitespace-nowrap">
            Not ranked
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-0.5 rounded-full bg-[#548B2E] text-white text-xs font-bold px-2.5 py-1 shadow-sm whitespace-nowrap"
            title={`Priority rank #${rank}`}
          >
            <Hash className="w-3 h-3" />
            {rank}
          </span>
        )}
      </div>
    </div>
  );
});

CategoryCard.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  productCount: PropTypes.number,
  rank: PropTypes.number,
  isUncategorized: PropTypes.bool,
  handle: PropTypes.node,
  isDragging: PropTypes.bool,
  style: PropTypes.object,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

// Draggable wrapper: wires @dnd-kit's sortable transform onto the card and
// exposes a grip handle so the edit/delete buttons stay independently clickable.
const SortableCategoryCard = ({ category, rank, productCount, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: category.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : undefined,
  };
  const handle = (
    <button
      type="button"
      className="p-1 -ml-1 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing touch-none rounded"
      title="Drag to reorder"
      aria-label={`Drag ${category.name} to reorder`}
      {...attributes}
      {...listeners}
    >
      <GripVertical className="w-5 h-5" />
    </button>
  );
  return (
    <CategoryCard
      ref={setNodeRef}
      style={style}
      category={category}
      rank={rank}
      productCount={productCount}
      handle={handle}
      isDragging={isDragging}
      onEdit={onEdit}
      onDelete={onDelete}
    />
  );
};

SortableCategoryCard.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  rank: PropTypes.number,
  productCount: PropTypes.number,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
};

// Delete Category Confirmation Modal
const DeleteCategoryModal = ({ category, assignedProducts, onConfirm, onClose }) => {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-xl">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800">Delete Category</h3>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete{' '}
            <span className="font-semibold text-gray-900">"{category.name}"</span>?
          </p>

          {assignedProducts.length > 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
              <p className="text-sm font-semibold text-amber-800">
                {assignedProducts.length} product{assignedProducts.length === 1 ? '' : 's'} will be moved to{' '}
                <span className="font-bold">Uncategorized</span>:
              </p>
              <ul className="space-y-1.5 max-h-48 overflow-y-auto">
                {assignedProducts.map(p => (
                  <li key={p.id} className="flex items-center gap-2 text-sm text-amber-900">
                    <Package className="w-3.5 h-3.5 flex-shrink-0 text-amber-600" />
                    {p.name}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-gray-500">This category has no products. It will be permanently deleted.</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 pb-6">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white px-4 py-2.5 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors font-semibold"
          >
            {loading
              ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              : <Trash2 className="w-4 h-4" />}
            {loading ? 'Deleting…' : 'Yes, Delete'}
          </button>
        </div>

      </div>
    </div>
  );
};

DeleteCategoryModal.propTypes = {
  category: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), name: PropTypes.string }),
  assignedProducts: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
  onConfirm: PropTypes.func,
  onClose: PropTypes.func,
};

// Category Modal Component
const CategoryModal = ({ category, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: category?.name || '',
    description: category?.description || '',
    id: category?.id || ''
  });
  const [base64Image, setBase64Image] = useState(null);
  const [imagePreview, setImagePreview] = useState(formatCategoryImageUrl(category?.imageUrl || category?.image_url) || '');
  const [imageError, setImageError] = useState(false);
  const [removeImage, setRemoveImage] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setFormData({
      name: category?.name || '',
      description: category?.description || '',
      id: category?.id || ''
    });
    setBase64Image(null);
    setImagePreview(formatCategoryImageUrl(category?.imageUrl || category?.image_url) || '');
    setImageError(false);
    setRemoveImage(false);
    setIsSaving(false);
  }, [category]);

  const processImageFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxDimension = 400;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxDimension) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          }
        } else {
          if (height > maxDimension) {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        setImagePreview(dataUrl);
        setBase64Image(dataUrl);
        setImageError(false);
        setRemoveImage(false);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processImageFile(file);
    }
  };

  const handleRemoveImage = () => {
    setBase64Image(null);
    setImagePreview('');
    setImageError(false);
    setRemoveImage(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;
    
    // Validation
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required';
    }
    if (formData.name.length < 2) {
      newErrors.name = 'Category name must be at least 2 characters';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim(),
    };
    if (base64Image) {
      payload.imageUrl = base64Image;
    } else if (removeImage) {
      payload.imageUrl = null;
    }

    try {
      setIsSaving(true);
      await onSave(payload);
    } catch (err) {
      // errors handled by caller / onSave
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h3 className="text-xl font-bold text-gray-800">
            {category ? 'Edit Category' : 'Add New Category'}
          </h3>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Category Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Leafy Greens & Herbs"
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E]/50 ${
                errors.name ? 'border-red-500' : 'border-gray-200'
              }`}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          {/* Category Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category Image
            </label>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-[#f2f8eb] border border-gray-200 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                <img
                  src={imagePreview && !imageError ? imagePreview : '/category_placeholder.png'}
                  alt="Preview"
                  className="w-full h-full object-cover"
                  onError={() => setImageError(true)}
                />
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  id="category-image-input"
                />
                <label
                  htmlFor="category-image-input"
                  className="inline-flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <Upload className="w-3.5 h-3.5" />
                  Choose File
                </label>
                {imagePreview && (
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="ml-2 text-xs text-red-500 hover:text-red-700 underline"
                  >
                    Remove
                  </button>
                )}
                <p className="text-xs text-gray-400 mt-1">PNG, JPG, WebP up to 5MB</p>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional description for this category"
              rows={3}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E]/50 resize-none"
            />
          </div>

          {/* ID Preview (for new categories) */}
          {!category && formData.name && (
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Category ID:</span>{' '}
                <code className="bg-gray-200 px-2 py-0.5 rounded text-xs">
                  {formData.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}
                </code>
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex-1 flex items-center justify-center gap-2 bg-[#548B2E] text-white px-4 py-2 rounded-lg hover:bg-[#3f6b20] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {isSaving ? 'Saving...' : (category ? 'Save Changes' : 'Add Category')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryManagement;